import { useEffect, useState } from "react";
import { TableCell, TableRow } from "@mui/material";
import OrderPreviewTableLayout, { OrderPreviewColumn, PreviewAccount, PreviewNumber } from "src/Components/OrderPreviewTableLayout";
import PreviewWarningsTable from "src/Tables/Order/PreviewWarningsTable";
import { ICCOrderAddOrderFormValues, ICCFileUploadOrderRow, ICCOrderCriterionTypes, ICCOrderRules, VolumeCalculationType } from "src/Interfaces/ccOrder";
import { DeltaHedgeOrderType } from "src/Interfaces/deltaHedgeOrder";
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";
import { CoveredMatrixExecutionMethod } from "src/Interfaces/coveredMatrixOrder";
import { IStepOptionDropdownItem, StepInstrumentType, StepOptionOrderType, StepOrderSide } from "src/Interfaces/stepInterfaces";
import { getCCOrderCriterionTypes, getCCOrderRules, getCCOrderUsers } from "src/Requests/ccOrder";
import { getStepInstruments } from "src/Requests/stepRequests";
import { getOptionInstruments } from "src/Requests/coveredCall";
import { fallbackCCOrderCriterionTypes, getCCOrderCriterionTypeName } from "src/Utils/ccOrderCriterionTypes";
import { getCCOrderVolumeCalculationOptions } from "src/Utils/ccOrderVolumeCalculationTypes";
import { packetErrorHandler } from "src/Utils/requestUtils";

interface IProps {
  values: (ICCOrderAddOrderFormValues | ICCFileUploadOrderRow)[] | null;
  coveredCallOrderType?: DeltaHedgeOrderType;
}

export const isCCOrderPreviewRowSendable = (value: Pick<ICCOrderAddOrderFormValues, "canSend" | "previewWarnings" | "totalVolume"> | null | undefined) =>
  !!value && typeof value.totalVolume === "number" && Number.isFinite(value.totalVolume) && value.totalVolume > 0 &&
  (value.canSend === true || (value.canSend == null && !(Array.isArray(value.previewWarnings) && value.previewWarnings.some((warning) => warning?.trim()))));

const processNames: Record<number, string> = { 1: "ورود", 2: "آفست", 3: "رول" };
const sendNames: Record<number, string> = { 0: "خودکار", 1: "اعتباری", 2: "غیر اعتباری" };
const volumeNames: Record<number, string> = { 1: "مقدار ثابت (تعداد قرارداد)", 2: "درصد پورتفو", 3: "درصد دارایی", 4: "درصد هدف پورتفو" };
const ordinalNames = ["اول", "دوم", "سوم"];
const lookup = (items: DropdownItem[] | undefined, id: unknown) => items?.find((item) => String(item.id) === String(id))?.name;

const OrderPreviewTable = ({ values, coveredCallOrderType }: IProps) => {
  const [rules, setRules] = useState<ICCOrderRules>();
  const [criteria, setCriteria] = useState<ICCOrderCriterionTypes>(fallbackCCOrderCriterionTypes);
  const [instruments, setInstruments] = useState<IStepOptionDropdownItem[]>([]);
  const [options, setOptions] = useState<DropdownItem[]>([]);
  const [accounts, setAccounts] = useState<DropdownItem[]>([]);

  useEffect(() => {
    getStepInstruments().then(setInstruments).catch(packetErrorHandler);
    getOptionInstruments().then(setOptions).catch(packetErrorHandler);
    getCCOrderRules().then(setRules).catch(packetErrorHandler);
    getCCOrderCriterionTypes().then(setCriteria).catch(packetErrorHandler);
    getCCOrderUsers([]).then(setAccounts).catch(packetErrorHandler);
  }, []);

  const rows = (values || []).filter(Boolean);
  const accountName = (value: ICCOrderAddOrderFormValues | ICCFileUploadOrderRow) =>
    value.accountName || value.userName || lookup(accounts, value.accountId) || `اکانت ${value.accountId}`;
  const warningRows = rows.flatMap((value) =>
    Array.isArray(value.coveredCallOrderBuyLegs) && Array.isArray(value.previewWarnings)
      ? value.previewWarnings.filter((warning) => warning?.trim()).map((warning) => ({
        userName: accountName(value),
        message: value.sheet ? `شیت ${value.sheet} · ردیف ${value.row ?? "—"}: ${warning}` : warning,
      }))
      : []);
  const legCount = rows.some((value) => Array.isArray(value.coveredCallOrderBuyLegs) && value.coveredCallOrderBuyLegs.length > 1) ? 3 : 2;
  const showExecution = rows.some((value) => Array.isArray(value.coveredCallOrderBuyLegs) && (value.executionMethod || value.manualBaseReferencePrice != null));
  const columns: OrderPreviewColumn[] = [
    { key: "row", label: "#", width: 30 },
    { key: "account", label: "نام اکانت", width: 100 },
    { key: "process", label: <>فرایند /<br />نوع جفت</>, width: 95 },
    ...Array.from({ length: legCount }, (_, index) => ({ key: `leg-${index}`, label: `نماد ${ordinalNames[index]}`, subtitle: "جهت / نوع معامله", width: 105 })),
    { key: "volumeMethod", label: <>روش تعیین<br />حجم</>, width: 105 },
    { key: "volumeInput", label: <>مقدار<br />ورودی حجم</>, width: 75 },
    { key: "volume", label: <>حجم نهایی<br />(تعداد قرارداد)</>, width: 85 },
    { key: "criterion", label: <>نوع و مقدار<br />معیار</>, width: 105 },
    { key: "block", label: <>بلوک<br />(%)</>, width: 50 },
    { key: "margin", label: <>مارجین<br />(%)</>, width: 50 },
    { key: "send", label: <>ترتیب<br />ارسال</>, width: 65 },
    ...(showExecution ? [{ key: "execution", label: <>روش اجرا /<br />قیمت مرجع پایه</>, width: 100 }] : []),
  ];

  const body = rows.map((value, index) => {
    const key = `${value.accountId}-${value.sheet || "form"}-${value.row ?? index}`;
    if (!Array.isArray(value.coveredCallOrderBuyLegs)) return <TableRow key={key}>
      <TableCell>{index + 1}</TableCell>
      <TableCell><PreviewAccount name="سفارش" sheet={value.sheet} row={value.row} canSend={false} /></TableCell>
      <TableCell colSpan={columns.length - 2}>{value.accessMessage || "اطلاعات این سفارش در دسترس نیست."}</TableCell>
    </TableRow>;
    const process = value.coveredCallOrderType ?? coveredCallOrderType;
    const volumeType = Number(value.originalVolumeCalculationType ?? value.volumeCalculationType ?? VolumeCalculationType.Static);
    const volumeLabel = lookup(getCCOrderVolumeCalculationOptions(rules?.volumeCalculationTypesByProcess?.[Number(process)]), volumeType) || volumeNames[volumeType] || "—";
    const originalVolume = value.originalVolumeInput ?? (volumeType === VolumeCalculationType.Static ? value.totalVolume : value.portfoPercent);
    const criterionLabel = getCCOrderCriterionTypeName(criteria, process, value.criterionType);
    const legs = [...(value.coveredCallOrderBuyLegs || []), value.coveredCallOrderSellLeg].filter(Boolean);
    return (
      <TableRow key={key}>
        <TableCell>{index + 1}</TableCell>
        <TableCell><PreviewAccount name={accountName(value)} sheet={value.sheet} row={value.row} canSend={isCCOrderPreviewRowSendable(value)} /></TableCell>
        <TableCell>
          <div>{processNames[Number(process)] || "—"}</div>
          <div className="order-preview-secondary">{lookup(rules?.pairTypeByProcessDropdown?.[Number(process)], value.coveredCallPositionStrategyEnum) || "—"}</div>
        </TableCell>
        {Array.from({ length: legCount }, (_, legIndex) => {
          const leg = legs[legIndex];
          if (!leg) return <TableCell key={legIndex}>—</TableCell>;
          const isOption = instruments.find((instrument) => String(instrument.id) === String(leg.instrumentId))?.type === StepInstrumentType.Option;
          return <TableCell key={legIndex}>
            <div>{value.instrumentNames?.[legIndex] || lookup(instruments, leg.instrumentId) || lookup(options, leg.instrumentId) || leg.instrumentId || "—"}</div>
            <div className="order-preview-secondary">{leg.orderSide === StepOrderSide.Buy ? "خرید" : leg.orderSide === StepOrderSide.Sell ? "فروش" : "—"}{leg.optionOrderType && isOption ? ` · ${leg.optionOrderType === StepOptionOrderType.Offset ? "Offset" : "Open"}` : ""}</div>
          </TableCell>;
        })}
        <TableCell>{volumeLabel}</TableCell>
        <TableCell><PreviewNumber value={originalVolume} suffix={volumeType !== VolumeCalculationType.Static ? " %" : ""} /></TableCell>
        <TableCell><PreviewNumber value={value.totalVolume} /></TableCell>
        <TableCell><div className="order-preview-secondary">{criterionLabel || "—"}</div><PreviewNumber value={value.criterion} /></TableCell>
        <TableCell><PreviewNumber value={value.blockSize} /></TableCell>
        <TableCell><PreviewNumber value={value.marginInPercent} /></TableCell>
        <TableCell>{sendNames[value.sendOrderType ?? 0] || "—"}</TableCell>
        {showExecution ? <TableCell>
          <div>{value.executionMethod ? value.executionMethod === CoveredMatrixExecutionMethod.BaseAlreadyOwned ? "موجود بودن نماد پایه" : value.executionMethod === CoveredMatrixExecutionMethod.FutureBasePurchase ? "قیمت ثابت نماد پایه" : "ارسال هر دو طرف" : "—"}</div>
          {value.manualBaseReferencePrice != null ? <div className="order-preview-secondary"><PreviewNumber value={value.manualBaseReferencePrice} /></div> : null}
        </TableCell> : null}
      </TableRow>
    );
  });
  return <div className="flex w-full min-w-0 flex-col items-center gap-3" dir="rtl">
    <PreviewWarningsTable rows={warningRows} />
    <OrderPreviewTableLayout title="پیش‌نمایش کاوردکال" count={rows.length} columns={columns} minWidth={legCount === 3 ? 1100 : 1000}><>{body}</></OrderPreviewTableLayout>
  </div>;
};

export default OrderPreviewTable;
